import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Request Password Reset Page
export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!res.ok) throw new Error('Failed to send reset email');
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center" data-testid="reset-email-sent">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-uf-text mb-2">Check Your Email</h1>
          <p className="text-uf-text-muted mb-6">
            If an account exists for <span className="text-uf-text">{email}</span>, you'll receive a password reset link shortly.
          </p>
          <p className="text-sm text-uf-text-muted mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>
          <Link to="/login" className="text-uf-red hover:underline">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8" data-testid="forgot-password-page">
        <Link to="/login" className="text-uf-text-muted hover:text-white flex items-center mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Link>
        
        <div className="w-16 h-16 bg-uf-red/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-uf-red" />
        </div>
        
        <h1 className="text-2xl font-bold text-uf-text text-center mb-2">Forgot Password?</h1>
        <p className="text-uf-text-muted text-center mb-6">
          Enter your email and we'll send you a reset link.
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-uf-text-muted mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 text-black placeholder-gray-400 focus:border-uf-red focus:outline-none"
              placeholder="you@example.com"
              data-testid="forgot-email-input"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || !email}
            className="w-full bg-uf-red text-white py-3 rounded-lg font-semibold hover:bg-uf-red-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Reset Password Page (with token)
export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-uf-text mb-2">Invalid Link</h1>
          <p className="text-uf-text-muted mb-6">
            This password reset link is invalid or has expired.
          </p>
          <Link to="/forgot-password" className="text-uf-red hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center" data-testid="password-reset-success">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-uf-text mb-2">Password Reset!</h1>
          <p className="text-uf-text-muted mb-6">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-uf-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-uf-red-hover"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8" data-testid="reset-password-page">
        <div className="w-16 h-16 bg-uf-red/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-uf-red" />
        </div>
        
        <h1 className="text-2xl font-bold text-uf-text text-center mb-2">Set New Password</h1>
        <p className="text-uf-text-muted text-center mb-6">
          Enter your new password below.
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-uf-text-muted mb-2">New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full bg-uf-bg border border-uf-border rounded-lg px-4 py-3 text-uf-text focus:border-uf-red focus:outline-none"
              placeholder="••••••••"
              data-testid="new-password-input"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm text-uf-text-muted mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              className="w-full bg-uf-bg border border-uf-border rounded-lg px-4 py-3 text-uf-text focus:border-uf-red focus:outline-none"
              placeholder="••••••••"
              data-testid="confirm-password-input"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || !password || !confirmPassword}
            className="w-full bg-uf-red text-white py-3 rounded-lg font-semibold hover:bg-uf-red-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Email Verification Page
export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'verifying' : 'no-token');
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (token && status === 'verifying') {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/email-verification/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      setStatus('success');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  if (status === 'no-token') {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-uf-text mb-2">Invalid Link</h1>
          <p className="text-uf-text-muted mb-6">
            This verification link is invalid.
          </p>
          <Link to="/hub" className="text-uf-red hover:underline">
            Go to Member Hub
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center">
          <Loader2 className="w-12 h-12 text-uf-red animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-uf-text mb-2">Verifying Email...</h1>
          <p className="text-uf-text-muted">Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center" data-testid="verify-email-error">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-uf-text mb-2">Verification Failed</h1>
          <p className="text-uf-text-muted mb-6">{error || 'This link may have expired or already been used.'}</p>
          <Link to="/hub" className="text-uf-red hover:underline">
            Go to Member Hub
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-uf-surface rounded-2xl border border-uf-border p-8 text-center" data-testid="verify-email-success">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-uf-text mb-2">Email Verified!</h1>
        <p className="text-uf-text-muted mb-6">
          Your email has been successfully verified. You now have full access to all features.
        </p>
        <Link 
          to="/hub" 
          className="inline-block bg-uf-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-uf-red-hover"
        >
          Go to Member Hub
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
